import { useState, useEffect } from "react";
import { FileText, Copy, Loader2, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fillTemplate(body, user) {
  if (!body) return body;
  const name = user?.name || "";
  return body
    .replace(/\{agent_name\}/g, name)
    .replace(/\{agent_phone\}/g, "")
    .replace(/\{property_title\}/g, "[titolo annuncio]")
    .replace(/\{zone\}/g, "[zona]")
    .replace(/\{price\}/g, "[prezzo]")
    .replace(/\{portal_name\}/g, "[portale]");
}

export default function TemplatePage({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editBody, setEditBody] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newBody, setNewBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/templates`).then(r => setTemplates(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const copy = (body) => {
    navigator.clipboard.writeText(fillTemplate(body, user));
    toast.success("Template copiato con il tuo nome");
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/templates/${id}`, { name: editName, body: editBody });
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, name: editName, body: editBody } : t));
      setEditing(null);
      toast.success("Template aggiornato");
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const createNew = async () => {
    if (!newName.trim() || !newBody.trim()) { toast.error("Compila nome e testo"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/templates`, { name: newName.trim(), body: newBody.trim(), portal_name: "_default" });
      setTemplates(prev => [...prev, res.data]);
      setShowNew(false);
      setNewName("");
      setNewBody("");
      toast.success("Template creato");
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Eliminare questo template?")) return;
    try {
      await axios.delete(`${API}/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success("Template eliminato");
    } catch { toast.error("Errore"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;

  return (
    <div data-testid="template-page" className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">Template Contatto</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Messaggi pronti da copiare — compilati con il tuo nome. Usa le variabili: <code className="text-[10px] bg-zinc-100 px-1 py-0.5 rounded">{"{agent_name}"} {"{zone}"} {"{portal_name}"} {"{price}"}</code></p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 px-4 text-xs font-semibold">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuovo Template
        </Button>
      </div>

      {/* New template form */}
      {showNew && (
        <div className="border-2 border-dashed border-zinc-300 rounded-sm p-4 space-y-3">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome del template" className="h-9 text-sm rounded-sm" />
          <textarea value={newBody} onChange={e => setNewBody(e.target.value)} placeholder={"Scrivi il testo del template...\nUsa {agent_name} per inserire automaticamente il tuo nome"} className="w-full border border-zinc-300 rounded-sm p-3 text-sm resize-none h-40 focus:outline-none focus:ring-1 focus:ring-zinc-400" />
          <div className="flex gap-2">
            <Button onClick={createNew} disabled={saving} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-8 px-4 text-xs">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Salva
            </Button>
            <button onClick={() => setShowNew(false)} className="text-xs text-zinc-500 hover:text-zinc-700">Annulla</button>
          </div>
        </div>
      )}

      {/* Templates list */}
      <div className="space-y-3">
        {templates.map(t => (
          <div key={t.id} className="border border-zinc-200 rounded-sm p-4">
            {editing === t.id ? (
              <div className="space-y-3">
                <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-9 text-sm rounded-sm font-semibold" />
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)} className="w-full border border-zinc-300 rounded-sm p-3 text-sm resize-none h-48 focus:outline-none focus:ring-1 focus:ring-zinc-400 font-mono" />
                <div className="flex gap-2">
                  <Button onClick={() => saveEdit(t.id)} disabled={saving} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-8 px-4 text-xs">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Salva
                  </Button>
                  <button onClick={() => setEditing(null)} className="text-xs text-zinc-500 hover:text-zinc-700">Annulla</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm font-bold text-zinc-800">{t.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copy(t.body)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-xs font-semibold border border-zinc-200 hover:bg-zinc-50 text-zinc-600 transition-colors">
                      <Copy className="w-3.5 h-3.5" /> Copia
                    </button>
                    <button onClick={() => { setEditing(t.id); setEditName(t.name); setEditBody(t.body); }} className="w-7 h-7 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50 text-zinc-400 hover:text-zinc-700">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteTemplate(t.id)} className="w-7 h-7 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-red-50 text-zinc-400 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <pre className="text-sm text-zinc-600 whitespace-pre-wrap bg-zinc-50 rounded-sm p-3 border border-zinc-100">{fillTemplate(t.body, user)}</pre>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
